// =============================================================================
// components/moderation/ReportBlockSheet.tsx — Trust & Safety UI (Apple 1.2) [A15]
//
// One reusable bottom sheet that powers REPORT + BLOCK + MUTE on every UGC
// surface (post, comment, user, DM, channel message). Built entirely on the A1
// glass contract (GlassSheet / GlassButton / GlassPill) — no new primitives.
//
// Flow:
//   1. Action menu — Report · Block @user · Mute @user (per target).
//   2. Report → reason picker (GlassPill grid) → confirm → calls the right
//      moderation service fn → "Thanks, we'll review" confirmation.
//   3. Block/Mute → confirm → service call → confirmation; onBlocked fires so
//      the host can drop the now-blocked user's content from view immediately.
//
// The host renders <ReportBlockSheet target={…} visible onClose /> controlled.
// =============================================================================
import React, { useState, useCallback } from 'react';
import { Modal, View, Text, Pressable, StyleSheet, ScrollView } from 'react-native';
// MIGRATION (bare RN): @expo/vector-icons -> the local Icon shim.
import { Ionicons, IoniconName } from '../ui/Icon';
import { GlassSheet, GlassButton, GlassPill } from '../glass';
import { colors, spacing, fonts } from '../../constants/theme';
import {
  REPORT_REASONS,
  ReportReason,
  ReportTargetType,
  reportPost,
  reportComment,
  reportUser,
  reportMessage,
  blockUser,
  muteUser,
} from '../../services/moderation';

export interface ReportTarget {
  type: ReportTargetType;
  // Content ids (for post/comment/message reports).
  contentId?: string;
  // The author/sender — id and/or username. Block/mute always act on the user.
  // At least one of authorId / authorUsername should be present to block.
  authorId?: string;
  authorUsername?: string;
  // Friendly label for the user, e.g. "@alice" or "Alice".
  authorLabel?: string;
  // Optional message text (for message reports → moderator context).
  text?: string;
  // Allow muting (default true for users/posts/comments; false where N/A).
  allowMute?: boolean;
}

interface Props {
  visible: boolean;
  target: ReportTarget | null;
  onClose: () => void;
  /** Fired after a successful block/mute so the host can hide the user's content. */
  onBlocked?: (target: ReportTarget) => void;
}

type Stage = 'menu' | 'reason' | 'done';

const CONTENT_NOUN: Record<ReportTargetType, string> = {
  post: 'post',
  comment: 'comment',
  user: 'account',
  message: 'message',
  channel_message: 'message',
};

export default function ReportBlockSheet({ visible, target, onClose, onBlocked }: Props) {
  const [stage, setStage] = useState<Stage>('menu');
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [busy, setBusy] = useState(false);
  const [doneText, setDoneText] = useState('');

  const reset = useCallback(() => {
    setStage('menu');
    setReason(null);
    setBusy(false);
    setDoneText('');
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  if (!target) {
    // Nothing to act on — render an inert modal so hooks stay stable.
    return <Modal visible={false} transparent onRequestClose={onClose} children={<View />} />;
  }

  const userTarget = target.authorId || target.authorUsername || '';
  const label = target.authorLabel || (target.authorUsername ? `@${target.authorUsername}` : 'this user');
  const noun = CONTENT_NOUN[target.type];
  const canBlock = !!userTarget;
  const canMute = canBlock && target.allowMute !== false;

  async function submitReport() {
    if (!reason || !target) return;
    setBusy(true);
    let res = { success: false, error: 'Unknown error' } as { success: boolean; error?: string };
    try {
      if (target.type === 'post' && target.contentId) {
        res = await reportPost(target.contentId, reason);
      } else if (target.type === 'comment' && target.contentId) {
        res = await reportComment(target.contentId, reason);
      } else if (target.type === 'user' && userTarget) {
        res = await reportUser(userTarget, reason);
      } else if ((target.type === 'message' || target.type === 'channel_message') && userTarget) {
        res = await reportMessage(
          userTarget,
          reason,
          target.text,
          target.type === 'channel_message' ? 'channel_message' : 'message',
        );
      }
    } catch (e: any) {
      res = { success: false, error: e?.message || 'Network error' };
    }
    setBusy(false);
    if (res.success) {
      setDoneText("Thanks — we'll review this and take action if it breaks our rules.");
      setStage('done');
    } else {
      setDoneText(res.error || 'Could not submit the report. Please try again.');
      setStage('done');
    }
  }

  async function doBlock() {
    if (!userTarget || !target) return;
    setBusy(true);
    const res = await blockUser(userTarget, { id: target.authorId, username: target.authorUsername });
    setBusy(false);
    if (res.success) {
      onBlocked?.(target);
      setDoneText(`${label} has been blocked. You won't see their content and they can't contact you.`);
    } else {
      setDoneText(res.error || 'Could not block this user. Please try again.');
    }
    setStage('done');
  }

  async function doMute() {
    if (!userTarget || !target) return;
    setBusy(true);
    const res = await muteUser(userTarget);
    setBusy(false);
    setDoneText(res.success ? `${label} muted. You won't see their content.` : (res.error || 'Could not mute. Please try again.'));
    setStage('done');
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={close}>
      <Pressable style={styles.backdrop} onPress={close} accessibilityLabel="Dismiss" />
      <View style={styles.sheetWrap} pointerEvents="box-none">
        <GlassSheet>
          {stage === 'menu' && (
            <View>
              <Text style={styles.title}>Report or block</Text>
              <Text style={styles.subtitle}>Keep CRYB safe. Your report is anonymous.</Text>

              <MenuRow
                icon="flag-outline"
                label={`Report ${noun}`}
                onPress={() => setStage('reason')}
              />
              {canBlock && (
                <MenuRow
                  icon="ban-outline"
                  label={`Block ${label}`}
                  destructive
                  onPress={doBlock}
                  disabled={busy}
                />
              )}
              {canMute && (
                <MenuRow
                  icon="volume-mute-outline"
                  label={`Mute ${label}`}
                  onPress={doMute}
                  disabled={busy}
                />
              )}
              <View style={{ height: spacing.sm }} />
              <GlassButton title="Cancel" variant="ghost" size="md" fullWidth onPress={close} />
            </View>
          )}

          {stage === 'reason' && (
            <View>
              <Text style={styles.title}>Why are you reporting this {noun}?</Text>
              <Text style={styles.subtitle}>Pick the closest reason.</Text>
              <ScrollView style={{ maxHeight: 260 }} showsVerticalScrollIndicator={false}>
                <View style={styles.reasonGrid}>
                  {REPORT_REASONS.map((r) => (
                    <GlassPill
                      key={r.code}
                      label={r.label}
                      active={reason === r.code}
                      onPress={() => setReason(r.code)}
                    />
                  ))}
                </View>
              </ScrollView>
              <View style={{ height: spacing.md }} />
              <GlassButton
                title="Submit report"
                variant="primary"
                size="lg"
                fullWidth
                loading={busy}
                disabled={!reason || busy}
                onPress={submitReport}
              />
              <View style={{ height: spacing.sm }} />
              <GlassButton title="Back" variant="ghost" size="md" fullWidth onPress={() => setStage('menu')} />
            </View>
          )}

          {stage === 'done' && (
            <View style={styles.doneWrap}>
              <Ionicons name="checkmark-circle" size={48} color={colors.primary} style={{ marginBottom: spacing.md }} />
              <Text style={styles.doneText}>{doneText}</Text>
              <View style={{ height: spacing.lg }} />
              <GlassButton title="Done" variant="primary" size="lg" fullWidth onPress={close} />
            </View>
          )}
        </GlassSheet>
      </View>
    </Modal>
  );
}

function MenuRow({
  icon,
  label,
  onPress,
  destructive,
  disabled,
}: {
  icon: IoniconName;
  label: string;
  onPress: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) {
  return (
    <Pressable
      style={({ pressed }) => [styles.menuRow, pressed && { opacity: 0.6 }, disabled && { opacity: 0.4 }]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Ionicons name={icon} size={20} color={destructive ? colors.error : colors.ink} />
      <Text style={[styles.menuLabel, destructive && { color: colors.error }]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(26,26,46,0.35)',
  },
  sheetWrap: {
    flex: 1,
    justifyContent: 'flex-end',
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  title: {
    fontSize: 18,
    color: colors.ink,
    ...fonts.semibold,
  },
  subtitle: {
    fontSize: 13,
    color: colors.muted,
    marginTop: 2,
    marginBottom: spacing.lg,
    ...fonts.regular,
  },
  menuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
  },
  menuLabel: {
    fontSize: 16,
    color: colors.ink,
    ...fonts.medium,
  },
  reasonGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  doneWrap: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  doneText: {
    fontSize: 15,
    color: colors.ink,
    textAlign: 'center',
    lineHeight: 22,
    ...fonts.regular,
  },
});
